import React, { useRef } from 'react';
import { X } from 'lucide-react';
import './SearchInput.scss';

export interface SearchInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> {
  value: string;
  onValueChange: (value: string) => void;
  // Applied to the positioning wrapper, not the input. Use for width caps
  // (e.g. the `--narrow` modifier) so the clear button tracks the input's edge.
  wrapperClassName?: string;
  clearAriaLabel?: string;
}

// The app's single search input. Wraps a normal <input> and adds a clear "×"
// that appears once there's text. The input keeps its caller-supplied className
// so it inherits whatever styling its context already gives it.
const SearchInput: React.FC<SearchInputProps> = ({
  value,
  onValueChange,
  wrapperClassName,
  clearAriaLabel = 'Clear search',
  className,
  style,
  disabled,
  ...rest
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const showClear = value.length > 0 && !disabled;

  const handleClear = () => {
    onValueChange('');
    inputRef.current?.focus();
  };

  return (
    <div className={`search-input-clearable${wrapperClassName ? ` ${wrapperClassName}` : ''}`}>
      <input
        {...rest}
        ref={inputRef}
        className={className}
        disabled={disabled}
        value={value}
        onChange={(event) => onValueChange(event.target.value)}
        // paddingRight is inline so it always wins over the input's own
        // class-based padding and text never runs under the clear button.
        style={{ width: '100%', paddingRight: '2rem', ...style }}
      />
      {showClear && (
        <button
          type="button"
          className="search-input-clearable__clear"
          aria-label={clearAriaLabel}
          // Keep focus on the input so clearing inside a combobox/dropdown
          // doesn't blur-close it before the click lands.
          onMouseDown={(event) => event.preventDefault()}
          onClick={handleClear}
        >
          <X size={16} aria-hidden />
        </button>
      )}
    </div>
  );
};

export default SearchInput;
